import React, { useEffect, useState, createElement } from 'react';
import { DollarSignIcon, TrendingUpIcon, CalendarIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, XCircleIcon, PrinterIcon, FileTextIcon, BanIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { mockClients, mockInvoices } from '../data/mockData';
import { Visit } from '../types';
import jsPDF from 'jspdf';
import { Pagination } from '../components/ui/Pagination';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
const LOGO_URL = "/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png";
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
const RECIBO_PREFIX = 'MR20';
const RECIBO_START_NUM = 2601;
// ── Helper: format number without currency symbol for tight PDF cells ────────
function fmtNum(n: number): string {
  return n.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
// ── Helper: calculate real ITBIS from facturasDetalle instead of assuming 18% ──
// If facturasDetalle has stored subTotal/itbis per invoice, use proportional ITBIS
// based on how much of each invoice was paid. Falls back to 0 if no ITBIS data.
function getRealItbisFromVisit(visit: Visit): {
  subTotal: number;
  itbis: number;
} {
  const montoTotal = visit.cobro.montoCobrado;
  const facturasDetalle: any[] = (visit.cobro as any).facturasDetalle || [];
  if (facturasDetalle.length > 0) {
    // Check if facturasDetalle has stored itbis data
    const hasItbisData = facturasDetalle.some((fd: any) => fd.subTotal !== undefined || fd.itbis !== undefined);
    if (hasItbisData) {
      // Calculate proportional ITBIS based on what was paid per invoice
      let totalItbis = 0;
      facturasDetalle.forEach((fd: any) => {
        const invSubTotal = fd.subTotal || 0;
        const invItbis = fd.itbis || 0;
        const invMonto = invSubTotal + invItbis;
        if (invMonto > 0 && invItbis > 0) {
          // Proportion of invoice that was paid (using abono = original amount before discount)
          const abono = fd.abono || fd.efectivoPagado || 0;
          const proportion = Math.min(abono / invMonto, 1);
          totalItbis += Math.round(invItbis * proportion * 100) / 100;
        }
        // If invItbis is 0, this invoice contributes 0 ITBIS
      });
      const subTotal = Math.round((montoTotal - totalItbis) * 100) / 100;
      return {
        subTotal: Math.max(0, subTotal),
        itbis: totalItbis
      };
    }
  }
  // Fallback for old data without itbis info: assume no ITBIS (safer than assuming 18%)
  // Old behavior was: subTotal = montoTotal / 1.18, itbis = montoTotal - subTotal
  // But this was wrong for invoices without ITBIS. Default to no ITBIS.
  return {
    subTotal: montoTotal,
    itbis: 0
  };
}
async function printRecibo(visit: Visit, receiptNumber: string) {
  const pw = 80;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, 297] // use longer page to avoid overflow, will trim
  });
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr; // 72mm
  const cx = pw / 2;
  const fmtCur = (n: number) => 'DOP ' + fmtNum(n);
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const dashedLine = (yPos: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, yPos, pw - mr, yPos);
    doc.setLineDashPattern([], 0);
  };
  const drawCheckbox = (x: number, yPos: number, checked: boolean) => {
    const size = 3;
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    doc.rect(x, yPos - size, size, size, 'FD');
    if (checked) {
      doc.setDrawColor(0, 100, 0);
      doc.setLineWidth(0.5);
      doc.line(x + 0.5, yPos - 1.5, x + 1.2, yPos - 0.5);
      doc.line(x + 1.2, yPos - 0.5, x + 2.7, yPos - 2.7);
      doc.setDrawColor(0);
      doc.setLineWidth(0.3);
    }
  };
  let y = 2;
  // ── LOGO + HEADER ─────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av. Antonio Guzmán No.102 • 809-247-1006', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('Santiago, Rep. Dom.', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = receiptNumber;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBO No: ' + receiptNum, ml, y);
  y += 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Fecha: ' + fmtDate(visit.fecha), ml, y);
  y += 5;
  dashedLine(y);
  y += 5;
  // ── CLIENT ────────────────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Recibido de:', ml, y);
  y += 4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  // Wrap long client names
  const clientLines = doc.splitTextToSize(visit.clienteNombre, cw);
  clientLines.forEach((line: string) => {
    doc.text(line, ml, y);
    y += 4;
  });
  y += 1;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Para ser abonado a su cuenta MILADYS RODRIGUEZ &', ml, y);
  y += 3.5;
  doc.text('Asociados, en la siguiente forma:', ml, y);
  y += 5;
  // ── INVOICE TABLE — 4 cols (optimized to fit 72mm) ─────────────────────────
  const tCols = ['FACTURA', 'FECHA', 'VALOR', 'MONTO'];
  const tWidths = [17, 15, 20, 20];
  const tRowH = 6;
  // Header row
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  let tx = ml;
  tCols.forEach((col, i) => {
    doc.text(col, tx + tWidths[i] / 2, y + tRowH - 1.5, {
      align: 'center'
    });
    tx += tWidths[i];
  });
  y += tRowH;
  // Data rows
  const facturasDetalle: any[] = (visit.cobro as any).facturasDetalle || [];
  const invoiceNums = visit.cobro.numeroFactura ? visit.cobro.numeroFactura.split(',').map(f => f.trim()).filter(Boolean) : [];
  const dataRows = facturasDetalle.length > 0 ? facturasDetalle : invoiceNums.length > 0 ? invoiceNums.map(n => ({
    numeroFactura: n,
    abono: 0,
    efectivoPagado: 0,
    descuento: 0,
    saldoNuevo: -1
  })) : [{
    numeroFactura: '—',
    abono: 0,
    efectivoPagado: 0,
    descuento: 0,
    saldoNuevo: -1
  }];

  // ── NC APPLICATIONS — pre-query to know which invoices have NCs ──────────
  let ncRows: any[] = [];
  const ncInvoiceNums = new Set<string>();
  // ── PP% from DB — fetch current pp_pct for invoices (may have been edited after visit) ──
  const invoicePPMap: Record<string, {
    ppPct: number | null;
    subTotal: number;
  }> = {};
  try {
    const factNums = dataRows.map((r: any) => typeof r === 'string' ? r : r.numeroFactura).filter((n: string) => n && n !== '—');
    if (factNums.length > 0) {
      const {
        data: ncApps
      } = await supabaseAdmin.from('nc_applications').select('id,farmacia,invoice_id,invoice_numero,monto,nc_monto_aplicado,estado,created_at').eq('farmacia', visit.clienteNombre).in('invoice_numero', factNums).order('created_at', {
        ascending: false
      });
      if (ncApps && ncApps.length > 0) {
        ncRows = ncApps;
        ncApps.forEach((nc: any) => {
          if (nc.invoice_numero) ncInvoiceNums.add(nc.invoice_numero);
        });
      }
      // Fetch current pp_pct and sub_total from invoices table
      const {
        data: invRows
      } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', factNums);
      if (invRows) {
        invRows.forEach((row: any) => {
          invoicePPMap[row.numero_factura] = {
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
            subTotal: Number(row.sub_total) || 0
          };
        });
      }
    }
  } catch {

    // Silently continue if query fails
  }
  let totalPPDiscount = 0;
  dataRows.forEach((row: any, idx: number) => {
    const inv = typeof row === 'string' ? row : row.numeroFactura;
    const hasNC = ncInvoiceNums.has(inv);
    const isEven = idx % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, tRowH, 'F');
    doc.setDrawColor(200, 210, 220);
    doc.setLineWidth(0.15);
    doc.rect(ml, y, cw, tRowH, 'S');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(hasNC ? 7 : 6);
    doc.setFont('helvetica', 'bold');
    // FACTURA — left-aligned with padding
    const maxFacW = tWidths[0] - 2;
    let facTxt = inv;
    while (doc.getTextWidth(facTxt) > maxFacW && facTxt.length > 3) facTxt = facTxt.slice(0, -1);
    if (facTxt !== inv) facTxt = facTxt.slice(0, -1) + '…';
    doc.text(facTxt, ml + 1, y + tRowH - 1.8);
    doc.setFont('helvetica', hasNC ? 'bold' : 'normal');
    // FECHA
    doc.text(fmtDate(visit.fecha), ml + tWidths[0] + tWidths[1] / 2, y + tRowH - 1.8, {
      align: 'center'
    });
    // VALOR (abono / crédito) — BOLD for thermal printer clarity
    const valorFila = row.abono > 0 ? row.abono : idx === 0 ? visit.cobro.credito : 0;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.text(valorFila > 0 ? fmtNum(valorFila) : '—', ml + tWidths[0] + tWidths[1] + tWidths[2] - 1, y + tRowH - 1.8, {
      align: 'right'
    });
    // Calculate discount BEFORE rendering MONTO so we can show adjusted amount
    let descFila = row.descuento || 0;
    if (descFila === 0 && inv && invoicePPMap[inv]) {
      const ppData = invoicePPMap[inv];
      if (ppData.ppPct != null && ppData.ppPct > 0 && ppData.subTotal > 0) {
        descFila = Math.round(ppData.subTotal * (ppData.ppPct / 100) * 100) / 100;
      }
    }
    totalPPDiscount += descFila;
    // MONTO (efectivo pagado — adjusted for discount)
    let montofila = row.efectivoPagado > 0 ? row.efectivoPagado : idx === 0 ? visit.cobro.montoCobrado : 0;
    // If there's a retroactive discount and the saved efectivoPagado didn't account for it,
    // show the adjusted amount (monto - descuento)
    if (descFila > 0 && row.descuento === 0 && montofila > 0) {
      montofila = Math.round((montofila - descFila) * 100) / 100;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.text(montofila > 0 ? 'DOP ' + fmtNum(montofila) : '—', ml + tWidths[0] + tWidths[1] + tWidths[2] + tWidths[3] - 1.5, y + tRowH - 1.8, {
      align: 'right'
    });
    y += tRowH;
    // Show discount sub-row
    if (descFila > 0) {
      doc.setFillColor(240, 253, 244);
      doc.rect(ml, y, cw, 4.5, 'F');
      doc.setDrawColor(200, 230, 210);
      doc.setLineWidth(0.1);
      doc.rect(ml, y, cw, 4.5, 'S');
      doc.setFontSize(5.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(5, 120, 60);
      const ppLabel = invoicePPMap[inv]?.ppPct ? ` ${invoicePPMap[inv].ppPct}%` : '';
      doc.text(`  Desc. PP${ppLabel}: -${fmtNum(descFila)}`, ml + 1, y + 3);
      doc.setTextColor(0, 0, 0);
      y += 4.5;
    }
  });
  // ── NC APPLICATIONS — display section ──────────────────────────────────────
  if (ncRows.length > 0) {
    y += 3;
    // NC section header — clear, bold, readable on thermal printer
    const ncHeaderH = 7;
    doc.setFillColor(240, 240, 240);
    doc.rect(ml, y, cw, ncHeaderH, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(ml, y, cw, ncHeaderH, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('NOTAS DE CRÉDITO APLICADAS', cx, y + ncHeaderH / 2 + 1, {
      align: 'center'
    });
    y += ncHeaderH;
    ncRows.forEach((nc: any, idx: number) => {
      const ncRowH = 7;
      doc.setFillColor(idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 250, 255);
      doc.rect(ml, y, cw, ncRowH, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.15);
      doc.rect(ml, y, cw, ncRowH, 'S');
      // NC number — bold, left
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(nc.credit_note_numero || 'NC', ml + 1.5, y + ncRowH / 2 + 1);
      // Fact reference — BOLD, middle
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(0, 0, 0);
      doc.text('F.Fact: ' + (nc.invoice_numero || ''), ml + 20, y + ncRowH / 2 + 1);
      // Amount — bold, right, negative
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(0, 0, 0);
      doc.text('-DOP ' + fmtNum(nc.nc_monto_aplicado || 0), ml + cw - 1.5, y + ncRowH / 2 + 1, {
        align: 'right'
      });
      doc.setTextColor(0, 0, 0);
      y += ncRowH;
    });
  }
  // ── DESCUENTO PP row (if any retroactive discount was calculated) ─────────
  if (totalPPDiscount > 0) {
    y += 2;
    const discRowH = 7;
    doc.setFillColor(240, 253, 244);
    doc.rect(ml, y, cw, discRowH, 'F');
    doc.setDrawColor(5, 150, 80);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, cw, discRowH, 'S');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('DESCUENTO PP:', ml + 3, y + discRowH / 2 + 1, {
      align: 'left'
    });
    doc.setFontSize(7);
    doc.text('-DOP ' + fmtNum(totalPPDiscount), ml + cw - 3, y + discRowH / 2 + 1, {
      align: 'right'
    });
    doc.setTextColor(0, 0, 0);
    y += discRowH;
  }
  // ── SUB TOTAL & ITBIS rows ───────────────────────────────────────────────
  const montoTotal = visit.cobro.montoCobrado;
  // Try to get real ITBIS from stored data first
  let {
    subTotal,
    itbis
  } = getRealItbisFromVisit(visit);
  // If no stored ITBIS data, query the actual invoices from DB
  if (itbis === 0 && montoTotal > 0) {
    try {
      const factNums = dataRows.map((r: any) => typeof r === 'string' ? r : r.numeroFactura).filter((n: string) => n && n !== '—');
      if (factNums.length > 0) {
        const {
          data: invoiceData
        } = await supabaseAdmin.from('invoices').select('numero_factura, sub_total, itbis, monto').in('numero_factura', factNums);
        if (invoiceData && invoiceData.length > 0) {
          let realItbis = 0;
          invoiceData.forEach((inv: any) => {
            const invItbis = inv.itbis || 0;
            const invMonto = inv.monto || 0;
            if (invItbis > 0 && invMonto > 0) {
              // Find matching factura detalle to get proportion paid
              const fd = dataRows.find((r: any) => r.numeroFactura === inv.numero_factura);
              const abono = fd ? fd.abono || fd.efectivoPagado || 0 : 0;
              const proportion = abono > 0 ? Math.min(abono / invMonto, 1) : 0;
              realItbis += Math.round(invItbis * proportion * 100) / 100;
            }
          });
          if (realItbis > 0) {
            itbis = realItbis;
            subTotal = Math.round((montoTotal - itbis) * 100) / 100;
          }
        }
      }
    } catch {

      // Keep the default values if query fails
    }
  }
  const summaryRowH = 7;
  y += 2;
  // Sub Total row — bold numbers for thermal printer
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, cw, summaryRowH, 'F');
  doc.setDrawColor(180, 190, 200);
  doc.setLineWidth(0.2);
  doc.rect(ml, y, cw, summaryRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('SUB TOTAL:', ml + 3, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFontSize(7);
  doc.text('DOP ' + fmtNum(subTotal), ml + cw - 3, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // ITBIS row — bold numbers for thermal printer
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, cw, summaryRowH, 'F');
  doc.setDrawColor(180, 190, 200);
  doc.setLineWidth(0.2);
  doc.rect(ml, y, cw, summaryRowH, 'S');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('ITBIS 18%:', ml + 3, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFontSize(7);
  doc.text('DOP ' + fmtNum(itbis), ml + cw - 3, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // TOTAL row — extra bold and larger for thermal printer
  const adjustedTotal = Math.max(0, Math.round((visit.cobro.montoCobrado - totalPPDiscount) * 100) / 100);
  const totalRowH = 9;
  doc.setFillColor(235, 240, 255);
  doc.rect(ml, y, cw, totalRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.rect(ml, y, cw, totalRowH, 'S');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TOTAL:', ml + 3, y + totalRowH / 2 + 1.5, {
    align: 'left'
  });
  doc.setFontSize(8.5);
  doc.text('DOP ' + fmtNum(adjustedTotal), ml + cw - 3, y + totalRowH / 2 + 1.5, {
    align: 'right'
  });
  y += totalRowH + 5;
  dashedLine(y);
  y += 5;
  // ── PAYMENT METHOD — drawn checkboxes ─────────────────────────────────────
  const metodo = visit.cobro.metodoPago || '';
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Forma de pago:', ml, y);
  y += 6;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(ml, y, metodo === 'efectivo');
  doc.text('Efectivo', ml + 4.5, y);
  drawCheckbox(ml + 26, y, metodo === 'cheque');
  doc.text('Cheque N°', ml + 30.5, y);
  if (metodo === 'cheque') {
    // Use cheques[] array if available, otherwise fall back to legacy single-cheque fields
    const chequesArr = visit.cobro.cheques && visit.cobro.cheques.length > 0 ? visit.cobro.cheques : [{
      numeroCheque: visit.cobro.numeroCheque || '',
      bancoCheque: visit.cobro.bancoCheque || '',
      fechaDeposito: visit.cobro.fechaDeposito || '',
      chequeImagen: null
    }];

    // Show first cheque number inline with the checkbox label
    const firstNum = chequesArr[0]?.numeroCheque || '___________';
    doc.setFont('helvetica', 'bold');
    doc.text(firstNum, ml + 48, y);
    doc.setFont('helvetica', 'normal');
    // Render each cheque's details
    chequesArr.forEach((ch, idx) => {
      if (chequesArr.length > 1) {
        // Label for multi-cheque
        y += 5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(`Cheque #${idx + 1}:`, ml + 4, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        if (idx > 0 && ch.numeroCheque) {
          doc.text(ch.numeroCheque, ml + 22, y);
        }
      }
      // Bank
      y += 5;
      doc.text('Banco:', ml + 4, y);
      if (ch.bancoCheque) {
        doc.setFont('helvetica', 'bold');
        doc.text(ch.bancoCheque, ml + 16, y);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.setLineWidth(0.2);
        doc.line(ml + 16, y + 0.5, pw - mr, y + 0.5);
      }
      // Deposit date
      y += 5;
      const fechaDep = ch.fechaDeposito ? new Date(ch.fechaDeposito + 'T00:00:00').toLocaleDateString('es-DO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) : '';
      doc.text('Fecha depósito:', ml + 4, y);
      if (fechaDep) {
        doc.setFont('helvetica', 'bold');
        doc.text(fechaDep, ml + 28, y);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.setLineWidth(0.2);
        doc.line(ml + 28, y + 0.5, pw - mr, y + 0.5);
      }
    });
  } else {
    doc.setLineWidth(0.2);
    doc.line(ml + 50, y + 0.5, pw - mr, y + 0.5);
  }
  y += 6;
  drawCheckbox(ml, y, metodo === 'transferencia');
  doc.text('Transferencia', ml + 4.5, y);
  if (metodo === 'transferencia') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 100, 50);
    doc.text('✓ Confirmada', ml + 30, y);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }
  y += 8;
  dashedLine(y);
  y += 7;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Conforme Cliente', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('Recibido Conforme', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 10;
  dashedLine(y);
  y += 6;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Gracias por su pago ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY = y;
  const barH = 10;
  let bx = ml;
  const seed = receiptNum.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w = seed * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx, barY, w, barH, 'F');
    }
    bx += w + 0.3;
    if (bx > ml + cw - 2) break;
  }
  y += barH + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: ' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function CollectionsPage() {
  const {
    user
  } = useAuth();
  // NOTE: We no longer pull the full `visits` array from AppContext.
  // Instead we fetch cobros directly with server-side filters — much faster.
  const {
    isLoading: appLoading
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  // ── OWN SERVER-SIDE COBROS FETCH ─────────────────────────────────────────
  // Queries ONLY cobro/cobro_y_venta visits with server-side date filter.
  // Before this change the page depended on AppContext loading ALL visit types
  // (seguimiento, venta, etc.) and then filtering client-side.
  const [cobroVisits, setCobroVisits] = useState<Visit[]>([]);
  const [cobroTotal, setCobroTotal] = useState(0);
  const [cobroLoading, setCobroLoading] = useState(true);

  // ── Totals across ALL filtered visits (not just current page) ──────────────
  const [totalsStats, setTotalsStats] = useState({
    totalCobrado: 0,
    pagados: 0,
    parciales: 0,
    pendientes: 0
  });
  const fetchTotals = React.useCallback(async (date: string, status: string) => {
    try {
      // Build the same filters as fetchCobros but without .range() — no page limit
      let q = supabaseAdmin.from('visits_lite').select('cobro,estado') // minimal columns — no image data
      .in('tipo_visita', ['cobro', 'cobro_y_venta']);
      if (date) {
        q = q.gte('fecha', `${date}T04:00:00.000Z`).lt('fecha', `${date}T28:00:00.000Z`);
      } else {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 180);
        q = q.gte('fecha', cutoff.toISOString());
      }
      if (status) q = q.eq('cobro->>estadoFactura', status);
      if (!isAdmin && user?.id) q = q.eq('vendedor_id', user.id);
      // No .range() — we need ALL matching rows for accurate totals
      const {
        data
      } = await q;
      if (!data) return;
      let total = 0,
        pagados = 0,
        parciales = 0,
        pendientes = 0;
      data.forEach((row: any) => {
        const cobro = row.cobro || {};
        if (cobro.anulado) return;
        if (!cobro.huboCobo || cobro.montoCobrado <= 0) return;
        const desc = cobro.descuento || 0;
        const monto = cobro.montoCobrado;
        total += desc > 0 ? monto : monto; // retroDiscountMap not available here; use raw monto
        const estado = cobro.estadoFactura;
        if (estado === 'pagada_completa') pagados++;else if (estado === 'pago_parcial') parciales++;else pendientes++;
      });
      setTotalsStats({
        totalCobrado: total,
        pagados,
        parciales,
        pendientes
      });
    } catch {
      // Silently keep previous totals on error
    }
  }, [isAdmin, user?.id]);
  const cobrosDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchCobros = React.useCallback(async (page: number, date: string, status: string) => {
    setCobroLoading(true);
    try {
      const COLS = 'id,vendedor_id,vendedor_nombre,cliente_id,cliente_nombre,zona,tipo_visita,fecha,hora_llegada,hora_salida,tiempo_total,cobro,venta,comentarios,motivo_no_pago,estado,numero_recibo,created_at';
      const rangeFrom = (page - 1) * ITEMS_PER_PAGE;
      const rangeTo = rangeFrom + ITEMS_PER_PAGE - 1;
      let q = supabaseAdmin.from('visits_lite').select(COLS, {
        count: 'exact'
      })
      // Only cobro types — avoids downloading seguimiento/venta rows
      .in('tipo_visita', ['cobro', 'cobro_y_venta']).order('fecha', {
        ascending: false
      }).range(rangeFrom, rangeTo);
      // Date filter: exact day in DR timezone (UTC-4)
      if (date) {
        q = q.gte('fecha', `${date}T04:00:00.000Z`).lt('fecha', `${date}T28:00:00.000Z`);
      } else {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 180); // 6-month default window
        q = q.gte('fecha', cutoff.toISOString());
      }
      if (status) q = q.eq('cobro->>estadoFactura', status);
      // Non-admins see only their own cobros
      if (!isAdmin && user?.id) q = q.eq('vendedor_id', user.id);
      const {
        data,
        error,
        count
      } = await q;
      if (error) {
        console.error('[CollectionsPage] fetchCobros error:', error);
        setCobroVisits([]);
        setCobroTotal(0);
      } else {
        const mapped = (data || []).map((row: any): Visit => ({
          id: row.id,
          vendedorId: row.vendedor_id,
          vendedorNombre: row.vendedor_nombre,
          clienteId: row.cliente_id,
          clienteNombre: row.cliente_nombre,
          zona: row.zona,
          tipoVisita: row.tipo_visita,
          fecha: new Date(row.fecha),
          horaLlegada: row.hora_llegada ? new Date(row.hora_llegada) : null,
          horaSalida: row.hora_salida ? new Date(row.hora_salida) : null,
          tiempoTotal: row.tiempo_total,
          cobro: {
            huboCobo: false,
            montoCobrado: 0,
            metodoPago: null,
            numeroFactura: '',
            estadoFactura: 'no_pago',
            credito: 0,
            descuento: 0,
            ...(row.cobro || {})
          },
          venta: {
            huboVenta: false,
            montoVenta: 0,
            numeroPedido: '',
            ...(row.venta || {})
          },
          comentarios: row.comentarios || '',
          motivoNoPago: row.motivo_no_pago || '',
          productosVencidos: null,
          devolucionItems: [],
          proximaVisita: null,
          estado: row.estado,
          numeroRecibo: row.numero_recibo || null
        }));
        setCobroVisits(mapped);
        setCobroTotal(count ?? 0);
      }
    } catch (err) {
      console.error('[CollectionsPage] fetchCobros exception:', err);
      setCobroVisits([]);
    } finally {
      setCobroLoading(false);
    }
  }, [isAdmin, user?.id]);

  // Totals query — runs only when FILTERS change, not on page navigation
  useEffect(() => {
    fetchTotals(dateFilter, statusFilter);
  }, [dateFilter, statusFilter, fetchTotals]);

  // Debounced re-fetch on filter/page changes
  useEffect(() => {
    if (cobrosDebounceRef.current) clearTimeout(cobrosDebounceRef.current);
    cobrosDebounceRef.current = setTimeout(() => {
      fetchCobros(currentPage, dateFilter, statusFilter);
    }, 300);
    return () => {
      if (cobrosDebounceRef.current) clearTimeout(cobrosDebounceRef.current);
    };
  }, [currentPage, dateFilter, statusFilter, fetchCobros]);

  // Use cobroVisits as the local "visits" so all the existing logic below still works
  const visits = cobroVisits;
  const isLoading_cobros = cobroLoading;
  const [isNewCobroOpen, setIsNewCobroOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Retroactive PP discount map: visitId → total discount amount
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    const timer = setTimeout(() => {
      const computeRetroDiscounts = async () => {
        // Collect all invoice numbers from all cobro visits
        const allFactNums: string[] = [];
        const visitFactMap: Record<string, string[]> = {};
        visits.forEach(v => {
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          if (fd.length > 0) {
            const nums = fd.map((f: any) => f.numeroFactura).filter(Boolean);
            if (nums.length > 0) {
              visitFactMap[v.id] = nums;
              allFactNums.push(...nums);
            }
          }
        });
        if (allFactNums.length === 0) return;
        const uniqueNums = [...new Set(allFactNums)];
        try {
          const {
            data: invRows
          } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
          if (!invRows) return;
          const ppMap: Record<string, {
            ppPct: number | null;
            subTotal: number;
          }> = {};
          invRows.forEach((row: any) => {
            ppMap[row.numero_factura] = {
              ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
              subTotal: Number(row.sub_total) || 0
            };
          });
          const newMap: Record<string, number> = {};
          Object.entries(visitFactMap).forEach(([visitId, nums]) => {
            const v = visits.find(vis => vis.id === visitId);
            if (!v) return;
            const fd: any[] = (v.cobro as any).facturasDetalle || [];
            let totalDisc = v.cobro.descuento || 0;
            // Only compute retro if saved descuento is 0
            if (totalDisc === 0) {
              nums.forEach(num => {
                const pp = ppMap[num];
                const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
                if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
                  totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
                }
              });
            }
            if (totalDisc > 0) newMap[visitId] = totalDisc;
          });
          setRetroDiscountMap(newMap);
        } catch {

          // Silently fail
        }
      };
      computeRetroDiscounts();
    }, 800);
    return () => clearTimeout(timer);
  }, [visits]);
  // Anular recibo state
  const [anularVisit, setAnularVisit] = useState<Visit | null>(null);
  const [isAnulando, setIsAnulando] = useState(false);
  const [anulados, setAnulados] = useState<Set<string>>(() => {
    // Load from visits that already have anulado flag
    const set = new Set<string>();
    visits.forEach(v => {
      if ((v.cobro as any)?.anulado) set.add(v.id);
    });
    return set;
  });
  const collectionVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta').filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      if (visitDate !== dateFilter) return false;
    }
    if (statusFilter && v.cobro.estadoFactura !== statusFilter) return false;
    return true;
  });
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(collectionVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    recibo: v => receiptNumberMap.get(v.id) || '',
    vendedor: v => v.vendedorNombre,
    cliente: v => v.clienteNombre,
    subTotal: v => getRealItbisFromVisit(v).subTotal,
    itbis: v => getRealItbisFromVisit(v).itbis,
    monto: v => v.cobro.montoCobrado,
    credito: v => v.cobro.credito,
    descuento: v => v.cobro.descuento,
    metodo: v => v.cobro.metodoPago || '',
    factura: v => v.cobro.numeroFactura || '',
    estado: v => v.cobro.estadoFactura || ''
  });
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  // totalPages uses the server-side count (cobroTotal), not the current page's row count
  const totalPages = Math.ceil(cobroTotal / ITEMS_PER_PAGE);
  // paginatedVisits is already the current page — no client-side slicing needed
  const paginatedVisits = finalVisits;
  // Helper: get effective cobro amount (accounting for saved + retroactive discounts)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  const activeVisits = finalVisits.filter(v => !(v.cobro as any)?.anulado && !anulados.has(v.id));
  // Totals come from fetchTotals (all filtered visits, not just current page)
  const totalCobrado = totalsStats.totalCobrado;
  const cobrosPagados = totalsStats.pagados;
  const cobrosParciales = totalsStats.parciales;
  const cobrosPendientes = totalsStats.pendientes;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return '-';
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cheque: 'Cheque'
    };
    return labels[method] || method;
  };
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'pagada_completa',
    label: 'Pagada Completa'
  }, {
    value: 'pago_parcial',
    label: 'Pago Parcial'
  }, {
    value: 'no_pago',
    label: 'Pendiente'
  }];
  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientId(e.target.value);
    setSelectedInvoices(new Set());
  };
  const pendingInvoices = selectedClientId ? mockInvoices.filter(inv => inv.clientId === selectedClientId && (inv.estado === 'pendiente' || inv.estado === 'vencida' || inv.estado === 'parcial')) : [];
  const toggleInvoiceSelection = (invoiceId: string) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) newSelection.delete(invoiceId);else newSelection.add(invoiceId);
    setSelectedInvoices(newSelection);
  };
  const totalSelectedAmount = pendingInvoices.filter(inv => selectedInvoices.has(inv.id)).reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const handleSubmitCobro = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsNewCobroOpen(false);
    }, 1000);
  };
  // Build a map of visit ID → sequential receipt number (MR202601, MR202602, ...)
  // Use DB field numero_recibo if available, otherwise generate sequentially
  const allCobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0).sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const receiptNumberMap = new Map<string, string>();
  allCobroVisits.forEach((v, idx) => {
    receiptNumberMap.set(v.id, v.numeroRecibo || RECIBO_PREFIX + (RECIBO_START_NUM + idx));
  });
  const getReceiptNumber = (visitId: string) => receiptNumberMap.get(visitId) || '';
  const handlePrintRecibo = async (visit: Visit) => {
    setPrintingId(visit.id);
    try {
      await printRecibo(visit, getReceiptNumber(visit.id));
    } finally {
      setPrintingId(null);
    }
  };
  const handleAnularCobro = async () => {
    if (!anularVisit) return;
    setIsAnulando(true);
    try {
      const updatedCobro = {
        ...anularVisit.cobro,
        anulado: true
      };
      await supabaseAdmin.from('visits').update({
        cobro: updatedCobro
      }).eq('id', anularVisit.id);
      setAnulados(prev => new Set([...prev, anularVisit.id]));
      setAnularVisit(null);
    } catch (err) {
      console.error('Error anulando cobro:', err);
    } finally {
      setIsAnulando(false);
    }
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-1155" data-mp="qyve74ihshei">
      <PageHeader title="Registro de Cobros" subtitle={`${cobroTotal} registros encontrados`} icon={<DollarSignIcon className="w-6 h-6" data-id="element-1157" data-mp="1bhro1ek211g9" />} data-id="element-1156" data-mp="mzcpdxnjvm6e">
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto" data-id="element-1158" data-mp="16i6n4ylbqj0w">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1160" data-mp="wl0xzvw9t23b" />} data-id="element-1159" data-mp="11iu0adugdbyb" />
          
          <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-1161" data-mp="1u78111cxemdk" />
          
          <Button variant="secondary" onClick={() => {
          setDateFilter('');
          setStatusFilter('');
        }} data-id="element-1162" data-mp="1p96hkfbl8l1k">
            
            Limpiar
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-1163" data-mp="2cyirxiiniz5g">
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1165" data-mp="2gg6eoyotn574" />} variant="success" data-id="element-1164" data-mp="287948ngny5w7" />
        
        <StatsCard title="Completos" value={cobrosPagados} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-1167" data-mp="2kjkzs3rmabrf" />} variant="primary" data-id="element-1166" data-mp="2khi8lgeqacl4" />
        
        <StatsCard title="Parciales" value={cobrosParciales} icon={<ClockIcon className="w-5 h-5" data-id="element-1169" data-mp="2uh93vkb44vm9" />} variant="warning" data-id="element-1168" data-mp="2pfa049bgnwos" />
        
        <StatsCard title="Pendientes" value={cobrosPendientes} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-1171" data-mp="33fb07mjwj037" />} variant="danger" data-id="element-1170" data-mp="2zdppa6qxgvvn" />
        
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-1172" data-mp="38844yafde8fa">
        {appLoading && visits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-1173" data-mp="3d54ckprvbj8i">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-1174" data-mp="3i58qq95b01vk" />
            <p className="text-slate-500 text-sm" data-id="element-1175" data-mp="3n3k5myagh2kn">Cargando cobros...</p>
          </div> : finalVisits.length === 0 ? <div className="p-12 text-center animate-fade-in" data-id="element-1176" data-mp="2xgzfzd3osgye">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1177" data-mp="2shmxt5nrfy25">
              <DollarSignIcon className="w-8 h-8 text-slate-400" data-id="element-1178" data-mp="3769at0px0dq7" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1179" data-mp="32fkhdfuod6of">
              No hay cobros
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-1180" data-mp="3g5wczoqgxnkb">
              No hay cobros registrados en el período seleccionado
            </p>
          </div> : <>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-slate-100" data-id="element-1181" data-mp="3q551i22ke9za">
              {paginatedVisits.map(visit => {
            const isPagada = visit.cobro.estadoFactura === 'pagada_completa';
            const isParcial = visit.cobro.estadoFactura === 'pago_parcial';
            const isPendiente = visit.cobro.estadoFactura === 'no_pago';
            const isPrinting = printingId === visit.id;
            const montoTotal = visit.cobro.montoCobrado;
            const {
              subTotal,
              itbis
            } = getRealItbisFromVisit(visit);
            return <div key={visit.id} className={`p-4 ${isPendiente ? 'bg-rose-50/40' : ''}`} data-id="element-1182" data-mp="3spb86hikcejj">
                  
                    <div className="flex items-start justify-between gap-2" data-id="element-1183" data-mp="1hp9l3apb29j">
                      <div className="flex-1 min-w-0" data-id="element-1184" data-mp="3jodd4oq3rhd3">
                        <p className="font-semibold text-slate-900 truncate" data-id="element-1185" data-mp="3jqpqeokx13jt">
                          {visit.clienteNombre}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-1186" data-mp="39rh1490zwd3s">
                          {visit.zona} · {formatDate(visit.fecha)}
                          {getReceiptNumber(visit.id) && <span className="ml-1 font-mono font-semibold text-[#2952C4]" data-id="element-1187" data-mp="17ysstl9huzh5">
                              · {getReceiptNumber(visit.id)}
                            </span>}
                        </p>
                        {isAdmin && <p className="text-xs text-slate-500 mt-0.5" data-id="element-1188" data-mp="342gsfpwgcps0">
                            {visit.vendedorNombre}
                          </p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1189" data-mp="2v1soxp4oq7td">
                        {isPagada && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-1190" data-mp="2q2z7xmq6vyog">
                            <CheckCircleIcon className="w-3 h-3" data-id="element-1191" data-mp="2l9mp5t86ym3m" /> Pagada
                          </span>}
                        {isParcial && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-1192" data-mp="2cc2oale8qrtj">
                            <ClockIcon className="w-3 h-3" data-id="element-1193" data-mp="22cv87qg4gj2q" /> Parcial
                          </span>}
                        {isPendiente && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-1194" data-mp="huaxi5txodt8">
                            <XCircleIcon className="w-3 h-3" data-id="element-1195" data-mp="rt9wsxst6x3k" /> Pendiente
                          </span>}
                        {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 && !anulados.has(visit.id) && <>
                              <button onClick={() => handlePrintRecibo(visit)} disabled={isPrinting} className="p-1.5 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors disabled:opacity-40" data-id="element-1196" data-mp="42we3xw46aln">
                          
                                {isPrinting ? <div className="w-4 h-4 border-2 border-[#aac1ef] border-t-[#2952C4] rounded-full animate-spin" data-id="element-1197" data-mp="3cher8e0wwgc4" /> : <PrinterIcon className="w-4 h-4" data-id="element-1198" data-mp="npt14pej41gy" />}
                              </button>
                              <button onClick={() => setAnularVisit(visit)} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1199" data-mp="iqgh0cf1l8i2">
                          
                                <BanIcon className="w-4 h-4" data-id="element-1200" data-mp="4s0aalh6jhzx" />
                              </button>
                            </>}
                        {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-1201" data-mp="dtq0jnzbsaw4">
                            <BanIcon className="w-3 h-3" data-id="element-1202" data-mp="1p71vtah0rl6o" /> Anulado
                          </span>}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-id="element-1203" data-mp="1l7t2u7tszrc6">
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-1204" data-mp="1snoamv1gzkuu">
                        <p className="text-slate-400 mb-0.5" data-id="element-1205" data-mp="1xom3c1k3vxal">Sub Total</p>
                        <p className="font-bold text-slate-700" data-id="element-1206" data-mp="1ir36ebx9yds2">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(subTotal) : <span className="text-slate-300" data-id="element-1207" data-mp="1nrzjng3inca5">—</span>}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-1208" data-mp="2gf3jz7rnz2fl">
                        <p className="text-slate-400 mb-0.5" data-id="element-1209" data-mp="21pxmppcdzmac">ITBIS 18%</p>
                        <p className="font-bold text-slate-500" data-id="element-1210" data-mp="21nux36f5bv65">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(itbis) : <span className="text-slate-300" data-id="element-1211" data-mp="2ud1v84aouiqv">—</span>}
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-2" data-id="element-1212" data-mp="z2trnly8pcq1">
                        <p className="text-emerald-600 mb-0.5 font-medium" data-id="element-1213" data-mp="z56jqrxpp3gt">
                          Total
                        </p>
                        <p className="font-bold text-emerald-700" data-id="element-1214" data-mp="qa8lr6fpj0im">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(montoTotal) : <span className="text-rose-400" data-id="element-1215" data-mp="1i1o60ttskb5o">Sin cobro</span>}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-1216" data-mp="1e0ugxx4m4qb7">
                        <p className="text-slate-400 mb-0.5" data-id="element-1217" data-mp="190gj17wq6wqw">Método</p>
                        <p className="font-medium text-slate-700" data-id="element-1218" data-mp="14259yze98q9q">
                          {getPaymentMethodLabel(visit.cobro.metodoPago)}
                        </p>
                      </div>
                    </div>
                    {(visit.cobro.credito > 0 || visit.cobro.descuento > 0) && <div className="mt-2 flex gap-2 text-xs" data-id="element-1219" data-mp="3eoh1eveg95gn">
                        {visit.cobro.credito > 0 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium" data-id="element-1220" data-mp="3jnabseyfwgcl">
                            Crédito: {formatCurrency(visit.cobro.credito)}
                          </span>}
                        {visit.cobro.descuento > 0 && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium" data-id="element-1221" data-mp="19kn9h6gwq1n">
                            Desc: {formatCurrency(visit.cobro.descuento)}
                          </span>}
                      </div>}
                    {visit.motivoNoPago && <p className="mt-2 text-xs text-rose-600" data-id="element-1222" data-mp="g73a2dzl4t6g">
                        ⚠ {visit.motivoNoPago}
                      </p>}
                  </div>;
          })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-1223" data-mp="2hygbgt2cgixr">
              <table className="w-full" data-id="element-1224" data-mp="s7bri72tb047">
                <thead data-id="element-1225" data-mp="s6a4d48iun64">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1226" data-mp="wyuvfy01mm5y">
                    <SortableTableHeader label="Fecha / Hora" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1227" data-mp="x0xut8n35rnc" />
                  
                    <SortableTableHeader label="No. Recibo" sortKey="recibo" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1228" data-mp="1bu9eccv9hqah" />
                  
                    {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1229" data-mp="3mof2qj0675lt" />}
                    <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1230" data-mp="3f4w97itavtyz" />
                  
                    <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1231" data-mp="3o3rij3rn5k6i" />
                  
                    <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1232" data-mp="35ekt6xwcdubq" />
                  
                    <SortableTableHeader label="Monto Cobrado" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1233" data-mp="gm938j9s2ogg" />
                  
                    <SortableTableHeader label="Crédito" sortKey="credito" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1234" data-mp="vbdsgkezpsy2" />
                  
                    <SortableTableHeader label="Descuento" sortKey="descuento" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1235" data-mp="bo7pmdv4fhcd" />
                  
                    <SortableTableHeader label="Método" sortKey="metodo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1236" data-mp="37lnc9cokn9yq" />
                  
                    <SortableTableHeader label="Factura" sortKey="factura" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1237" data-mp="2tt7byjn21q1i" />
                  
                    <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1238" data-mp="2fwbuq3wngsad" />
                  
                    <th className="px-5 py-4 w-12" data-id="element-1239" data-mp="31yhwm326418g"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1240" data-mp="36x2zka8t0z0l">
                  {paginatedVisits.map(visit => {
                const isPagada = visit.cobro.estadoFactura === 'pagada_completa';
                const isParcial = visit.cobro.estadoFactura === 'pago_parcial';
                const isPendiente = visit.cobro.estadoFactura === 'no_pago';
                const isPrinting = printingId === visit.id;
                const montoTotal = visit.cobro.montoCobrado;
                const {
                  subTotal,
                  itbis
                } = getRealItbisFromVisit(visit);
                return <tr key={visit.id} className={`hover:bg-slate-50/50 transition-colors ${isPendiente ? 'bg-rose-50/30' : ''}`} data-id="element-1241" data-mp="2e9p9mw3ix3pz">
                      
                        <td className="px-5 py-4" data-id="element-1242" data-mp="256xtwk3m7lsj">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-1243" data-mp="259tru8fgqagz">
                            {formatDate(visit.fecha)}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-1244" data-mp="1goe7hay3wrdk">
                            {formatTime(visit.horaLlegada)}
                          </p>
                        </td>
                        <td className="px-5 py-4" data-id="element-1245" data-mp="1qk8xir4ntoqd">
                          {getReceiptNumber(visit.id) ? <span className="font-mono text-sm font-semibold text-[#2952C4]" data-id="element-1246" data-mp="24k096l7pet17">
                              {getReceiptNumber(visit.id)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1247" data-mp="2eb4lfd5zvzsc">—</span>}
                        </td>
                        {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-1248" data-mp="2nbj7wfup3kxf">
                            {visit.vendedorNombre}
                          </td>}
                        <td className="px-5 py-4" data-id="element-1249" data-mp="2j7uzxw25inbn">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-1250" data-mp="2qdxi8eopy2b3">
                            {visit.clienteNombre}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-1251" data-mp="2v9waidd644gs">{visit.zona}</p>
                          {visit.motivoNoPago && <p className="text-xs text-rose-600 mt-0.5" data-id="element-1252" data-mp="352k6x708k6ez">
                              ⚠ {visit.motivoNoPago}
                            </p>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1253" data-mp="3e1xiq6bu172v">
                          {visit.cobro.huboCobo && montoTotal > 0 ? <span className="text-sm text-slate-600" data-id="element-1254" data-mp="3j2cu4sck04i7">
                              {formatCurrency(subTotal)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1255" data-mp="1oobvo8kx1jin">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1256" data-mp="1tnohcq01wkjn">
                          {visit.cobro.huboCobo && montoTotal > 0 ? <span className="text-sm text-slate-500" data-id="element-1257" data-mp="atvdxpwd3cx4">
                              {formatCurrency(itbis)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1258" data-mp="20ies67pff2l">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1259" data-mp="3t7l5yxnjllct">
                          {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 ? <span className="text-lg font-bold text-emerald-600" data-id="element-1260" data-mp="pq2y4q7rsece">
                              {formatCurrency(visit.cobro.montoCobrado - (retroDiscountMap[visit.id] && visit.cobro.descuento === 0 ? retroDiscountMap[visit.id] : 0))}
                            </span> : <span className="text-sm font-medium text-rose-400" data-id="element-1261" data-mp="30jfi4216zg8r">
                              Sin cobro
                            </span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1262" data-mp="ufs25ebkovah">
                          {visit.cobro.credito > 0 ? <span className="text-sm font-semibold text-blue-600" data-id="element-1263" data-mp="zelj5gq2j4fe">
                              {formatCurrency(visit.cobro.credito)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1264" data-mp="1s3iwr6jc4d1t">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1265" data-mp="1dfebpvwt7pfv">
                          {(() => {
                      const disc = retroDiscountMap[visit.id] || visit.cobro.descuento || 0;
                      return disc > 0 ? <span className="text-sm font-semibold text-emerald-600" data-id="element-1266" data-mp="2aus9rpsbdjy5">
                                {formatCurrency(disc)}
                              </span> : <span className="text-slate-300 text-sm" data-id="element-1267" data-mp="3qm4948tm3tru">—</span>;
                    })()}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1268" data-mp="94b64m5hym0g">
                          {visit.cobro.metodoPago ? <span className="text-sm text-slate-600" data-id="element-1269" data-mp="47ukpzpomlr2">
                              {getPaymentMethodLabel(visit.cobro.metodoPago)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-1270" data-mp="37o1eneiap4mn">—</span>}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1271" data-mp="3ljwsv71r1rjp">
                          {visit.cobro.facturasDetalle && visit.cobro.facturasDetalle.length > 0 ? <div className="flex flex-col gap-1.5 items-center" data-id="element-1272" data-mp="10y3lloeco3ii">
                              {visit.cobro.facturasDetalle!.map((fd, i) => <div key={i} className="w-full text-left border border-slate-100 rounded-lg px-2 py-1.5 bg-slate-50" data-id="element-1273" data-mp="lms1jf52xc51" data-mpi={i}>
                            
                                  <div className="flex items-center justify-between gap-2" data-id="element-1274" data-mp="vmixll9afz78">
                                    <span className="text-xs font-bold text-slate-700" data-id="element-1275" data-mp="qlv66yh5dgch">
                                      {fd.numeroFactura}
                                    </span>
                                    <span className="text-xs font-semibold text-emerald-600" data-id="element-1276" data-mp="10jrezjmm3z7f">
                                      {formatCurrency(fd.efectivoPagado)}
                                    </span>
                                  </div>
                                  {fd.descuento > 0 && <div className="text-xs text-emerald-500 mt-0.5" data-id="element-1277" data-mp="1obgcdeeajdv8">
                                      Desc: {formatCurrency(fd.descuento)}
                                    </div>}
                                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-1278" data-mp="22xvhlgggsxq4">
                                    Saldo:{' '}
                                    {fd.saldoNuevo === 0 ? <span className="text-emerald-500 font-medium" data-id="element-1279" data-mp="1sy4q7nn7ayu9">
                                        ✓ Pagada
                                      </span> : formatCurrency(fd.saldoNuevo)}
                                  </div>
                                </div>)}
                            </div> : visit.cobro.numeroFactura ? <div className="flex flex-col gap-1 items-center" data-id="element-1280" data-mp="1f79joi06cttu">
                              {visit.cobro.numeroFactura.split(',').map((f: string, i: number) => <span key={i} className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1281" data-mp="158abt1n8h27w" data-mpi={i}>
                            
                                    {f.trim()}
                                  </span>)}
                            </div> : <span className="text-slate-300 text-sm" data-id="element-1282" data-mp="7g2kf6w1fc3v">—</span>}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1283" data-mp="152jfs6l7nfmz">
                          {isPagada && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-1284" data-mp="105jbo17x8myi">
                              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1285" data-mp="q6bxxcx9yqav" /> Pagada
                            </span>}
                          {isParcial && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-1286" data-mp="3fwxatmvnf412">
                              <ClockIcon className="w-3.5 h-3.5" data-id="element-1287" data-mp="1qf9x0nd1j2m8" /> Parcial
                            </span>}
                          {isPendiente && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-1288" data-mp="20cofstmfs3hp">
                              <XCircleIcon className="w-3.5 h-3.5" data-id="element-1289" data-mp="2546efb0zlrzi" /> Pendiente
                            </span>}
                        </td>
                        <td className="px-3 py-4 text-center" data-id="element-1290" data-mp="2e3jpu98o8qmv">
                          <div className="flex items-center justify-center gap-1" data-id="element-1291" data-mp="2e65w85jtt03i">
                            {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 && !anulados.has(visit.id) && <>
                                  <button onClick={() => handlePrintRecibo(visit)} title="Imprimir recibo" disabled={isPrinting} className="p-1.5 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors disabled:opacity-40" data-id="element-1292" data-mp="oqzfslibzzn0">
                              
                                    {isPrinting ? <div className="w-4 h-4 border-2 border-[#aac1ef] border-t-[#2952C4] rounded-full animate-spin" data-id="element-1293" data-mp="2p3wmmsicmlxk" /> : <PrinterIcon className="w-4 h-4" data-id="element-1294" data-mp="21fn542s64kc8" />}
                                  </button>
                                  <button onClick={() => setAnularVisit(visit)} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1295" data-mp="1wdxvmrklgeq9">
                              
                                    <BanIcon className="w-4 h-4" data-id="element-1296" data-mp="1dpsttpnhz3u3" />
                                  </button>
                                </>}
                            {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-1297" data-mp="18d46vdgnc4jl">
                                <BanIcon className="w-3 h-3" data-id="element-1298" data-mp="tlbv1bg298mm" /> Anulado
                              </span>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </>}
      </Card>

      <div className="grid grid-cols-3 gap-3 animate-stagger-3" data-id="element-1299" data-mp="1cefoowta3lh8">
        <Card data-id="element-1300" data-mp="1cg0n4xiprokd">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-1301" data-mp="1hch9bm6crsut">
            <div className="p-2.5 bg-[#eef2fc] rounded-xl flex-shrink-0" data-id="element-1302" data-mp="yhc4iyw6hb9">
              <TrendingUpIcon className="w-5 h-5 text-[#2952C4]" data-id="element-1303" data-mp="10lolpauwon5" />
            </div>
            <div data-id="element-1304" data-mp="5z5axqtd42g1">
              <p className="text-xs text-slate-500" data-id="element-1305" data-mp="5vhhernx3g1b">Efectivo</p>
              <p className="text-base font-bold text-slate-900" data-id="element-1306" data-mp="28ixwpc43auxf">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'efectivo').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
        <Card data-id="element-1307" data-mp="1ow9zutwoqbwu">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-1308" data-mp="1ow05ku4lxilc">
            <div className="p-2.5 bg-indigo-50 rounded-xl flex-shrink-0" data-id="element-1309" data-mp="1kuoiowd4bbgu">
              <TrendingUpIcon className="w-5 h-5 text-indigo-600" data-id="element-1310" data-mp="1kwrha4sc6cx6" />
            </div>
            <div data-id="element-1311" data-mp="1fvtz3s7btivo">
              <p className="text-xs text-slate-500" data-id="element-1312" data-mp="1fxeu1ixjz3u2">Transferencia</p>
              <p className="text-base font-bold text-slate-900" data-id="element-1313" data-mp="1ay1vw218eac6">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'transferencia').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
        <Card data-id="element-1314" data-mp="3hsugyhmfdvel">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-1315" data-mp="3hscaks61u0sy">
            <div className="p-2.5 bg-violet-50 rounded-xl flex-shrink-0" data-id="element-1316" data-mp="3mjastxall8yu">
              <DollarSignIcon className="w-5 h-5 text-violet-600" data-id="element-1317" data-mp="3mm6thtdtybqz" />
            </div>
            <div data-id="element-1318" data-mp="3rl03vcxtlmmx">
              <p className="text-xs text-slate-500" data-id="element-1319" data-mp="2tv6lz8gaipuk">Cheque</p>
              <p className="text-base font-bold text-slate-900" data-id="element-1320" data-mp="2twq2n05uz2wv">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'cheque').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Modal isOpen={isNewCobroOpen} onClose={() => setIsNewCobroOpen(false)} title="Registrar Nuevo Cobro" size="lg" data-id="element-1321" data-mp="j3n8nbxhe867">
        
        <ModalBody data-id="element-1322" data-mp="o4b5ie8e5j8c">
          <div className="space-y-6" data-id="element-1323" data-mp="195zlb0bz26i5">
            <div data-id="element-1324" data-mp="1970x570tax0x">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1325" data-mp="196irxkw698gv">
                Seleccionar Cliente
              </label>
              <div className="relative" data-id="element-1326" data-mp="1ivsjmzn3sp4g">
                <select value={selectedClientId} onChange={handleClientChange} className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-1327" data-mp="1iwu9494v9e5p">
                  
                  <option value="" data-id="element-1328" data-mp="1nulyave4mm67">Seleccione una farmacia...</option>
                  {mockClients.map(client => <option key={client.id} value={client.id} data-id="element-1329" data-mp="21su2nodhspre">
                      {client.nombre}
                    </option>)}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-500" data-id="element-1330" data-mp="7hot63fq5j45">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1331" data-mp="2jn85cyqn9q1">
                    
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-1332" data-mp="2j3h4j5fj8o03" />
                    
                  </svg>
                </div>
              </div>
            </div>

            {selectedClientId && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-1333" data-mp="20dr9qibrs286">
                <div className="flex items-center justify-between mb-3" data-id="element-1334" data-mp="20benhsyzb5pb">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2" data-id="element-1335" data-mp="1libei9zge0a7">
                    <FileTextIcon className="w-4 h-4 text-slate-500" data-id="element-1336" data-mp="1qg3elra94sph" /> Facturas
                    Pendientes
                  </h3>
                  {pendingInvoices.length > 0 && <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full" data-id="element-1337" data-mp="3ksjgygzxfmp2">
                      {pendingInvoices.length} facturas
                    </span>}
                </div>
                {pendingInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-1338" data-mp="yx0jz2r2op3a">
                    <CheckCircleIcon className="w-10 h-10 text-emerald-400 mx-auto mb-3" data-id="element-1339" data-mp="13paih7696xh6" />
                    <h4 className="text-slate-900 font-medium mb-1" data-id="element-1340" data-mp="18o4zq4okodz6">
                      Todo al día
                    </h4>
                    <p className="text-slate-500 text-sm" data-id="element-1341" data-mp="1dkvdglcwmxhf">
                      Este cliente no tiene facturas pendientes de cobro.
                    </p>
                  </div> : <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-1342" data-mp="1mm1xh4eriqff">
                    <div className="max-h-[300px] overflow-y-auto" data-id="element-1343" data-mp="3jgg9fe8syxev">
                      <table className="w-full text-sm" data-id="element-1344" data-mp="3oe8064xpodhh">
                        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-1345" data-mp="3oeq4zpyfw010">
                          <tr data-id="element-1346" data-mp="3gw9sm5z3bcgw">
                            <th className="px-4 py-3 w-10" data-id="element-1347" data-mp="3c48nd40cam0d">
                              <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={selectedInvoices.size === pendingInvoices.length && pendingInvoices.length > 0} onChange={() => {
                          if (selectedInvoices.size === pendingInvoices.length) setSelectedInvoices(new Set());else setSelectedInvoices(new Set(pendingInvoices.map(i => i.id)));
                        }} data-id="element-1348" data-mp="3c2nt7fhxt531" />
                          
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-1349" data-mp="2btz8swhrndi2">
                              Factura
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-1350" data-mp="21x2w0eku457p">
                              Vencimiento
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-500" data-id="element-1351" data-mp="1t50v6f6usmww">
                              Estado
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500" data-id="element-1352" data-mp="1xy3tf0mkskjf">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1353" data-mp="1a6yfws43y4ca">
                          {pendingInvoices.map(inv => {
                      const isSelected = selectedInvoices.has(inv.id);
                      const daysOverdue = Math.floor((new Date().getTime() - new Date(inv.fechaVencimiento).getTime()) / (1000 * 60 * 60 * 24));
                      const isOverdue = daysOverdue > 0;
                      return <tr key={inv.id} className={`transition-colors cursor-pointer ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`} onClick={() => toggleInvoiceSelection(inv.id)} data-id="element-1354" data-mp="zk9si49hfq9s">
                            
                                <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-1355" data-mp="1nd9tnyulwcop">
                              
                                  <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={isSelected} onChange={() => toggleInvoiceSelection(inv.id)} data-id="element-1356" data-mp="1x9moj01w0wl1" />
                              
                                </td>
                                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1357" data-mp="2q418jyg1nmn7">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-4 py-3" data-id="element-1358" data-mp="1xeubz505h74l">
                                  <div className="text-slate-600" data-id="element-1359" data-mp="1xfdvgq5g83kq">
                                    {formatDate(inv.fechaVencimiento)}
                                  </div>
                                  {isOverdue && <div className="text-xs text-rose-600 font-medium mt-0.5" data-id="element-1360" data-mp="27axe4av055rj">
                                      {daysOverdue} días vencida
                                    </div>}
                                </td>
                                <td className="px-4 py-3 text-center" data-id="element-1361" data-mp="174k6qwltlt07">
                                  {inv.estado === 'vencida' ? <Badge variant="danger" size="sm" data-id="element-1362" data-mp="128cy2w21i3ek">
                                      Vencida
                                    </Badge> : inv.estado === 'parcial' ? <Badge variant="warning" size="sm" data-id="element-1363" data-mp="oec6qndf3io8">
                                      Parcial
                                    </Badge> : <Badge variant="info" size="sm" data-id="element-1364" data-mp="je7oarsy8dvd">
                                      Pendiente
                                    </Badge>}
                                </td>
                                <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-1365" data-mp="9jpfj88ods4p">
                                  {formatCurrency(inv.saldoPendiente)}
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-between" data-id="element-1366" data-mp="6xyhfc1nskqt">
                      <div className="text-sm text-slate-500" data-id="element-1367" data-mp="3oezofms9lsre">
                        {selectedInvoices.size} facturas seleccionadas
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1368" data-mp="kw6f5m6irq31">
                        <span className="text-sm font-semibold text-slate-700" data-id="element-1369" data-mp="kuv9fgtik89a">
                          Total a Cobrar:
                        </span>
                        <span className="text-xl font-bold text-emerald-600" data-id="element-1370" data-mp="buyh957x0r9h">
                          {formatCurrency(totalSelectedAmount)}
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1371" data-mp="30p5d59nlgc99">
          <Button variant="secondary" onClick={() => setIsNewCobroOpen(false)} data-id="element-1372" data-mp="30lhjmai5fpuj">
            Cancelar
          </Button>
          <Button onClick={handleSubmitCobro} isLoading={isSubmitting} disabled={!selectedClientId || selectedInvoices.size === 0} leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-1374" data-mp="3304p9k5pb68v" />} data-id="element-1373" data-mp="2o5q53gaehqsm">
            
            Registrar Cobro
          </Button>
        </ModalFooter>
      </Modal>

      {/* Anular Recibo Modal */}
      <Modal isOpen={!!anularVisit} onClose={() => setAnularVisit(null)} title="Anular Recibo de Cobro" size="sm" data-id="element-1375" data-mp="2abpexaxilc3v">
        
        <ModalBody data-id="element-1376" data-mp="2k3v8gp3f01l6">
          <div className="text-center space-y-3" data-id="element-1377" data-mp="2k568qexnir7j">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-1378" data-mp="2fd6ff5c36wjx">
              <BanIcon className="w-7 h-7 text-rose-500" data-id="element-1379" data-mp="1rn2cl06q30tz" />
            </div>
            <p className="text-slate-600" data-id="element-1380" data-mp="1rkzctofrptc2">
              ¿Estás seguro de anular el recibo de cobro de{' '}
              <strong data-id="element-1381" data-mp="114wavtqpoggc">{anularVisit?.clienteNombre}</strong> por{' '}
              <strong className="text-emerald-600" data-id="element-1382" data-mp="116qw795waaky">
                {anularVisit ? formatCurrency(anularVisit.cobro.montoCobrado) : ''}
              </strong>
              ?
            </p>
            <p className="text-xs text-slate-400" data-id="element-1383" data-mp="1b5gev45m7z29">
              Esta acción marcará el recibo como anulado. No se puede deshacer.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1384" data-mp="1k3iebf9pajnf">
          <Button variant="secondary" onClick={() => setAnularVisit(null)} data-id="element-1385" data-mp="1p2va4pijv125">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularCobro} isLoading={isAnulando} leftIcon={<BanIcon className="w-4 h-4" data-id="element-1387" data-mp="1yrd5usbz2hyx" />} data-id="element-1386" data-mp="1ttto3ig2fn28">
            
            Anular Recibo
          </Button>
        </ModalFooter>
      </Modal>

      <div className="animate-stagger-4" data-id="element-1388" data-mp="22sf5vbdpqy1y">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={cobroTotal} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1389" data-mp="27sl4jbiehktj" />
        
      </div>
    </div>;
}